import { Link } from '@tanstack/react-router';
import { type FormEvent, useState, useSyncExternalStore } from 'react';
import { NotificationsPanel } from '../components/NotificationsPanel';
import { PasskeysPanel } from '../components/Passkeys';
import { ApiError, api } from '../lib/api';
import { useMe } from '../lib/session';
import { getThemePref, setThemePref, subscribeTheme, THEME_TITLES, type ThemePref } from '../lib/theme';
import { Button } from '../ui/button';
import { ErrorBox, toast } from '../ui/feedback';
import { Field, Input } from '../ui/form';
import { KeyValue, Page, PageHeader, Panel } from '../ui/layout';

/** Тема оформления (НФ-17): выбор хранится в этом браузере */
function ThemePanel() {
  const pref = useSyncExternalStore(subscribeTheme, getThemePref, () => 'light' as ThemePref);
  return (
    <Panel title="Оформление">
      <fieldset>
        <legend className="mb-3 text-[14px] text-ink-2">
          Тёмная тема удобна вечером и на площадке. Выбор действует в этом браузере.
        </legend>
        <div className="flex flex-wrap gap-2">
          {(Object.keys(THEME_TITLES) as ThemePref[]).map((k) => (
            <label
              key={k}
              className={
                pref === k
                  ? 'inline-flex h-10 cursor-pointer items-center gap-2 rounded-md border border-ink bg-surface px-3 text-[14px] font-medium text-ink'
                  : 'inline-flex h-10 cursor-pointer items-center gap-2 rounded-md border border-line-strong bg-surface px-3 text-[14px] text-ink-2 hover:text-ink'
              }
            >
              <input
                type="radio"
                name="theme"
                value={k}
                checked={pref === k}
                onChange={() => setThemePref(k)}
                className="accent-[var(--color-ink)]"
              />
              {THEME_TITLES[k]}
            </label>
          ))}
        </div>
      </fieldset>
    </Panel>
  );
}

/** Выход на других устройствах: потерянный телефон, чужой компьютер (НФ-11) */
function SessionsPanel() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  async function endOthers() {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ ok: true; ended: number }>('/auth/logout-others', { body: {} });
      toast(r.ended ? `Завершено сеансов на других устройствах: ${r.ended}` : 'Других сеансов не было');
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Panel title="Сеансы входа">
      <p className="mb-4 max-w-prose text-[14px] text-ink-2">
        Если вход остался на чужом компьютере или потерян телефон, завершите все сеансы, кроме этого. На
        других устройствах понадобится войти заново.
      </p>
      <ErrorBox error={error} className="mb-3" />
      <Button loading={busy} onClick={endOthers}>
        Выйти на других устройствах
      </Button>
    </Panel>
  );
}

export function ProfilePage() {
  const me = useMe();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    setError(null);
    if (next !== repeat) {
      setErrors({ repeat: 'Пароли не совпадают' });
      return;
    }
    setBusy(true);
    try {
      await api('/auth/password', { body: { currentPassword: current, newPassword: next } });
      setCurrent('');
      setNext('');
      setRepeat('');
      toast('Пароль изменён. Другие сеансы входа завершены.');
    } catch (err) {
      if (err instanceof ApiError) setErrors(err.fieldErrors());
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page>
      <PageHeader title="Профиль" subtitle={me.user.email} />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel title="Учётная запись">
          <KeyValue
            items={[
              { label: 'Имя', value: me.user.fullName },
              { label: 'Почта', value: me.user.email },
              { label: 'Должность', value: me.user.position },
              { label: 'Компания', value: me.tenant?.name },
              {
                label: 'Роли',
                value: me.roles.length
                  ? me.roles.map((r) => (r.scopeName ? `${r.name} (${r.scopeName})` : r.name)).join(', ')
                  : me.tenant?.isOwner
                    ? 'Владелец — все права'
                    : 'не назначены',
              },
            ]}
          />
        </Panel>
        <Panel title="Смена пароля">
          <form onSubmit={submit} className="flex max-w-sm flex-col gap-4" noValidate>
            <Field label="Текущий пароль" htmlFor="cur">
              <Input
                id="cur"
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
              />
            </Field>
            <Field
              label="Новый пароль"
              htmlFor="new"
              hint="Не короче 10 символов, буквы и цифры"
              error={errors.newPassword}
            >
              <Input
                id="new"
                type="password"
                autoComplete="new-password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
              />
            </Field>
            <Field label="Новый пароль ещё раз" htmlFor="rep" error={errors.repeat}>
              <Input
                id="rep"
                type="password"
                autoComplete="new-password"
                value={repeat}
                onChange={(e) => setRepeat(e.target.value)}
              />
            </Field>
            <ErrorBox error={Object.keys(errors).length ? null : error} />
            <Button type="submit" variant="primary" loading={busy} disabled={!current || !next}>
              Сменить пароль
            </Button>
            {/* Входят по Face ID и забыли пароль — ссылка на почту (ДС-19); у общей демо-записи пароля нет */}
            {me.user.demoRole ? null : (
              <p className="text-[13px] text-ink-2">
                Не помните текущий пароль?{' '}
                <Link
                  to="/forgot-password"
                  search={{ email: me.user.email }}
                  className="inline-flex min-h-11 items-center font-medium text-ink underline underline-offset-4"
                >
                  Получить ссылку на почту
                </Link>
              </p>
            )}
          </form>
        </Panel>
        <NotificationsPanel />
        <PasskeysPanel />
        <SessionsPanel />
        <ThemePanel />
      </div>
    </Page>
  );
}
